import { css, etiColors, mediaQueries } from '@eti/styles';
import { useCallback, useEffect, useState, useRef } from 'react';
import { vars } from '@etg/wings';
import { useDirection } from '@eti/providers';
import useDeepCompareEffect from 'use-deep-compare-effect';
import isFunction from 'lodash/isFunction';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const containerStyles = css`
  align-items: center;
  display: flex;
  height: 24px;
  justify-content: center;
  margin-bottom: 13px;
  padding-left: 5%;
  padding-right: 5%;
  position: relative;

  @media ${mediaQueries.medium.up} {
    padding-left: 0;
    padding-right: 0;
  }
`;
const sliderStyles = css`
  position: relative;
  width: 100%;
`;
const trackStyles = css`
  background-color: ${vars.colors.inputs.main};
  border-radius: 5px;
  height: 4px;
  position: absolute;
  width: 100%;
  z-index: 1;
`;
const rangeStyles = css`
  background-color: #ccc;
  border-radius: 5px;
  height: 4px;
  position: absolute;
  z-index: 2;
`;
const sliderValueStyles = isLtR => css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.875rem;
  margin-top: 22px;
  position: absolute;
  ${isLtR ? 'left: 0' : 'right: -1px'}
`;
const thumbStyles = css`
  /* stylelint-disable-next-line */
  -webkit-appearance: none;
  height: 0;
  outline: none;
  pointer-events: none;
  position: absolute;
  width: 100%;

  ::-webkit-slider-thumb {
    /* stylelint-disable-next-line */
    -webkit-appearance: none;
    background-color: ${etiColors.white};
    border: 1px solid ${etiColors.grayScale[7]};
    border-radius: 50%;
    cursor: pointer;
    height: 28px;
    margin-top: 2px;
    pointer-events: all;
    position: relative;
    -webkit-tap-highlight-color: transparent;
    width: 28px;
    z-index: 3;
  }

  &:focus::-webkit-slider-thumb {
    outline: 3px solid ${vars.colors.inputs.focus};
    outline-offset: 0.125rem;
  }

  ::-moz-range-thumb {
    background-color: ${etiColors.white};
    border: 1px solid ${etiColors.grayScale[7]};
    border-radius: 50%;
    cursor: pointer;
    height: 28px;
    margin-top: 2px;
    pointer-events: all;
    position: relative;
    width: 28px;
    z-index: 3;
  }

  &:focus::-moz-range-thumb {
    outline: ${vars.colors.inputs.focus};
    outline-offset: 0.125rem;
  }
`;
const SingleSlider = _ref => {
  let {
    formatDisplayedValue,
    handleValuesChange,
    maxValue,
    minValue,
    initialValue = minValue,
    handleValues = [initialValue],
    step = (maxValue - minValue) / 10
  } = _ref;
  const {
    direction
  } = useDirection();
  const [inputValue, setInputValue] = useState(initialValue);
  const range = useRef(null);
  const isLeftToRight = direction === 'ltr';
  const formatValue = value => isFunction(formatDisplayedValue) ? formatDisplayedValue(value) : value;

  // Convert to percentage
  const getPercent = useCallback(value => Math.round((value - minValue) / (maxValue - minValue) * 100), [minValue, maxValue]);

  // Set width of the range
  useEffect(() => {
    const valuePercent = getPercent(inputValue);
    const maxPercent = getPercent(maxValue);
    if (range.current && isLeftToRight) {
      range.current.style.left = `${valuePercent}%`;
      range.current.style.width = `${maxPercent - valuePercent}%`;
    }
    if (range.current && !isLeftToRight) {
      range.current.style.right = `${valuePercent}%`;
      range.current.style.width = `${maxPercent - valuePercent}%`;
    }
  }, [getPercent, isLeftToRight, inputValue, maxValue]);

  // Used to reset filter
  useDeepCompareEffect(() => {
    setInputValue(handleValues[0]);
  }, [handleValues]);
  return /*#__PURE__*/_jsxs("div", {
    className: containerStyles,
    children: [/*#__PURE__*/_jsx("input", {
      "aria-label": formatValue(inputValue),
      "aria-valuenow": inputValue,
      "aria-valuetext": formatValue(inputValue),
      className: thumbStyles,
      max: maxValue,
      min: minValue,
      onChange: event => {
        setInputValue(Math.round(Number(event.target.value)));
      },
      onKeyUp: event => {
        if (event.keyCode === 37 || event.keyCode === 39) {
          handleValuesChange([inputValue]);
        }
      },
      onMouseUp: () => {
        handleValuesChange([inputValue]);
      },
      onTouchEnd: () => {
        handleValuesChange([inputValue]);
      },
      step: step,
      type: "range",
      value: inputValue
    }), /*#__PURE__*/_jsxs("div", {
      className: sliderStyles,
      children: [/*#__PURE__*/_jsx("div", {
        className: trackStyles
      }), /*#__PURE__*/_jsx("div", {
        className: rangeStyles,
        ref: range
      }), /*#__PURE__*/_jsx("div", {
        className: sliderValueStyles(isLeftToRight),
        children: formatValue(inputValue)
      })]
    })]
  });
};
export default SingleSlider;
//# sourceMappingURL=SingleSlider.js.map